import React, { useState } from 'react';
import { PageLayout } from '../shared/PageLayout';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { SettingsIcon, ShieldIcon, BellIcon, DatabaseIcon, PaletteIcon, InfoIcon, CheckIcon, ChevronRightIcon } from 'lucide-react';
interface SettingToggleProps {
  label: string;
  description: string;
  value: boolean;
  onChange: (v: boolean) => void;
}
function SettingToggle({
  label,
  description,
  value,
  onChange
}: SettingToggleProps) {
  return <div className="flex items-center justify-between py-4 border-b border-gray-50 last:border-0" data-id="element-634">
      <div className="flex-1 pr-4" data-id="element-635">
        <div className="text-sm font-medium text-gray-900" data-id="element-636">{label}</div>
        <div className="text-xs text-gray-500 mt-0.5" data-id="element-637">{description}</div>
      </div>
      <button onClick={() => onChange(!value)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${value ? 'bg-blue-600' : 'bg-gray-200'}`} role="switch" aria-checked={value} data-id="element-638">
        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${value ? 'translate-x-4.5' : 'translate-x-0.5'}`} style={{
        transform: value ? 'translateX(18px)' : 'translateX(2px)'
      }} data-id="element-639" />
      </button>
    </div>;
}
interface SettingSelectProps {
  label: string;
  description: string;
  value: string;
  options: Array<{
    value: string;
    label: string;
  }>;
  onChange: (v: string) => void;
}
function SettingSelect({
  label,
  description,
  value,
  options,
  onChange
}: SettingSelectProps) {
  return <div className="flex items-center justify-between py-4 border-b border-gray-50 last:border-0" data-id="element-640">
      <div className="flex-1 pr-4" data-id="element-641">
        <div className="text-sm font-medium text-gray-900" data-id="element-642">{label}</div>
        <div className="text-xs text-gray-500 mt-0.5" data-id="element-643">{description}</div>
      </div>
      <select value={value} onChange={e => onChange(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" data-id="element-644">
        {options.map(opt => <option key={opt.value} value={opt.value} data-id="element-645">
            {opt.label}
          </option>)}
      </select>
    </div>;
}
const sections = [{
  id: 'general',
  label: 'Algemeen',
  icon: <SettingsIcon className="w-4 h-4" data-id="element-646" />
}, {
  id: 'governance',
  label: 'Governance',
  icon: <ShieldIcon className="w-4 h-4" data-id="element-647" />
}, {
  id: 'notifications',
  label: 'Meldingen',
  icon: <BellIcon className="w-4 h-4" data-id="element-648" />
}, {
  id: 'data',
  label: 'Data & Opslag',
  icon: <DatabaseIcon className="w-4 h-4" data-id="element-649" />
}] as const;
type SectionId = (typeof sections)[number]['id'];
const ADMIN_SETTINGS_KEY = 'meester-djay-admin-settings';
function loadPersistedSettings(): Record<string, any> {
  try {
    const raw = localStorage.getItem(ADMIN_SETTINGS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // Ignore parse errors
  }
  return {};
}
function persistSettings(settings: Record<string, any>) {
  try {
    localStorage.setItem(ADMIN_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Ignore quota errors
  }
}
export function AdminSettingsPage() {
  const [activeSection, setActiveSection] = useState<SectionId>('general');
  const [saved, setSaved] = useState(false);
  // Settings state — load from localStorage on mount
  const [settings, setSettings] = useState(() => {
    const persisted = loadPersistedSettings();
    return {
      // General
      strictMode: persisted.strictMode ?? false,
      debugLogging: persisted.debugLogging ?? false,
      showConnectionBadges: persisted.showConnectionBadges ?? true,
      language: persisted.language ?? 'nl',
      // Governance
      autoValidate: persisted.autoValidate ?? true,
      blockOnError: persisted.blockOnError ?? false,
      warnOnWarning: persisted.warnOnWarning ?? true,
      governanceLevel: persisted.governanceLevel ?? 'standard',
      // Notifications
      notifyOnError: persisted.notifyOnError ?? true,
      notifyOnWarning: persisted.notifyOnWarning ?? false,
      notifyOnNewAccount: persisted.notifyOnNewAccount ?? true,
      // Data
      persistSessions: persisted.persistSessions ?? true,
      maxSessionHistory: persisted.maxSessionHistory ?? '1000',
      autoCleanup: persisted.autoCleanup ?? false
    };
  });
  const update = (key: keyof typeof settings, value: boolean | string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleSave = () => {
    persistSettings(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  return <PageLayout title="Instellingen" description="Systeem- en governance-instellingen" actions={<div className="flex items-center gap-2" data-id="element-651">
          <ConnectionBadge status="live" data-id="element-652" />
          <button onClick={handleSave} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${saved ? 'bg-emerald-600 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-653">
            {saved ? <>
                <CheckIcon className="w-4 h-4" data-id="element-654" />
                Opgeslagen
              </> : 'Opslaan'}
          </button>
        </div>} data-id="element-650">
      <div className="flex flex-col md:flex-row gap-6" data-id="element-655">
        {/* Sidebar nav */}
        <div className="w-full md:w-48 flex-shrink-0" data-id="element-656">
          <nav className="flex md:flex-col gap-2 overflow-x-auto pb-2 md:pb-0 md:space-y-1" data-id="element-657">
            {sections.map(section => <button key={section.id} onClick={() => setActiveSection(section.id)} className={`flex-shrink-0 flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === section.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`} data-id="element-658">
                {section.icon}
                {section.label}
              </button>)}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-659">
          {activeSection === 'general' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-660">
              <div className="px-5 py-4 border-b border-gray-100" data-id="element-661">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-662">
                  Algemene instellingen
                </h2>
              </div>
              <div className="px-5" data-id="element-663">
                <SettingToggle label="Strict mode" description="Striktere validatie en foutafhandeling" value={settings.strictMode} onChange={v => update('strictMode', v)} data-id="element-664" />
                <SettingToggle label="Debug logging" description="Uitgebreide logging in de browser console" value={settings.debugLogging} onChange={v => update('debugLogging', v)} data-id="element-665" />
                <SettingToggle label="Verbindingsbadges tonen" description="Toon live/mock badges op elke pagina" value={settings.showConnectionBadges} onChange={v => update('showConnectionBadges', v)} data-id="element-666" />
                <SettingSelect label="Taal" description="Taal van de admin interface" value={settings.language} options={[{
              value: 'nl',
              label: 'Nederlands'
            }, {
              value: 'en',
              label: 'English'
            }]} onChange={v => update('language', v)} data-id="element-667" />
              </div>
            </div>}

          {activeSection === 'governance' && <div className="space-y-4" data-id="element-668">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-669">
                <div className="px-5 py-4 border-b border-gray-100" data-id="element-670">
                  <h2 className="text-sm font-semibold text-gray-900" data-id="element-671">
                    Governance pipeline
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-672">
                    Instellingen voor de validatie- en governance-pipeline
                  </p>
                </div>
                <div className="px-5" data-id="element-673">
                  <SettingToggle label="Automatisch valideren" description="Valideer vragen automatisch bij het laden" value={settings.autoValidate} onChange={v => update('autoValidate', v)} data-id="element-674" />
                  <SettingToggle label="Blokkeren bij fouten" description="Blokkeer het systeem bij governance-fouten" value={settings.blockOnError} onChange={v => update('blockOnError', v)} data-id="element-675" />
                  <SettingToggle label="Waarschuwen bij warnings" description="Toon waarschuwingen bij governance-issues" value={settings.warnOnWarning} onChange={v => update('warnOnWarning', v)} data-id="element-676" />
                  <SettingSelect label="Governance niveau" description="Strengheid van de governance-regels" value={settings.governanceLevel} options={[{
                value: 'lenient',
                label: 'Licht (alleen fouten)'
              }, {
                value: 'standard',
                label: 'Standaard (fouten + warnings)'
              }, {
                value: 'strict',
                label: 'Strikt (alles)'
              }]} onChange={v => update('governanceLevel', v)} data-id="element-677" />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-4 flex items-start gap-3" data-id="element-678">
                <InfoIcon className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-679" />
                <div data-id="element-680">
                  <p className="text-sm font-medium text-blue-800" data-id="element-681">
                    Governance pipeline
                  </p>
                  <p className="text-xs text-blue-600 mt-1" data-id="element-682">
                    De governance pipeline controleert alle vragen op
                    schema-validatie, type-regels, inhoudskwaliteit en visuele
                    governance. Zie de <strong data-id="element-683">Validatieregels</strong> pagina
                    voor een volledige compliance-check.
                  </p>
                </div>
              </div>
            </div>}

          {activeSection === 'notifications' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-684">
              <div className="px-5 py-4 border-b border-gray-100" data-id="element-685">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-686">
                  Meldingsinstellingen
                </h2>
              </div>
              <div className="px-5" data-id="element-687">
                <SettingToggle label="Melden bij fouten" description="Stuur een melding bij governance-fouten" value={settings.notifyOnError} onChange={v => update('notifyOnError', v)} data-id="element-688" />
                <SettingToggle label="Melden bij waarschuwingen" description="Stuur een melding bij governance-waarschuwingen" value={settings.notifyOnWarning} onChange={v => update('notifyOnWarning', v)} data-id="element-689" />
                <SettingToggle label="Melden bij nieuw account" description="Stuur een melding bij een nieuw account" value={settings.notifyOnNewAccount} onChange={v => update('notifyOnNewAccount', v)} data-id="element-690" />
              </div>
            </div>}

          {activeSection === 'data' && <div className="space-y-4" data-id="element-691">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-692">
                <div className="px-5 py-4 border-b border-gray-100" data-id="element-693">
                  <h2 className="text-sm font-semibold text-gray-900" data-id="element-694">
                    Data & opslag
                  </h2>
                </div>
                <div className="px-5" data-id="element-695">
                  <SettingToggle label="Sessies bewaren" description="Bewaar oefensessies in lokale opslag" value={settings.persistSessions} onChange={v => update('persistSessions', v)} data-id="element-696" />
                  <SettingToggle label="Automatisch opruimen" description="Verwijder oude sessies automatisch" value={settings.autoCleanup} onChange={v => update('autoCleanup', v)} data-id="element-697" />
                  <SettingSelect label="Max. sessiegeschiedenis" description="Maximum aantal sessies om te bewaren" value={settings.maxSessionHistory} options={[{
                value: '100',
                label: '100 sessies'
              }, {
                value: '500',
                label: '500 sessies'
              }, {
                value: '1000',
                label: '1.000 sessies'
              }, {
                value: '5000',
                label: '5.000 sessies'
              }]} onChange={v => update('maxSessionHistory', v)} data-id="element-698" />
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-start gap-3" data-id="element-699">
                <InfoIcon className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-700" />
                <div data-id="element-701">
                  <p className="text-sm font-medium text-amber-800" data-id="element-702">
                    Lokale opslag
                  </p>
                  <p className="text-xs text-amber-600 mt-1" data-id="element-703">
                    Alle data wordt lokaal opgeslagen in de browser. Er is geen
                    externe database verbinding. Data gaat verloren bij het
                    wissen van de browser-cache.
                  </p>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </PageLayout>;
}